import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import CodeMirrorEditor, { type CodeMirrorEditorProps } from './CodeMirrorEditor';

const parseLinter = jsonParseLinter();

// Lint JSON syntax inline, but treat an empty/whitespace document as valid so optional
// fields (e.g. follow-up template components) don't show a spurious error when blank.
const emptyOkJsonLinter = linter((view) => {
  if (!view.state.doc.toString().trim()) return [];
  return parseLinter(view);
});

const STATIC_EXTENSIONS: Extension[] = [json(), emptyOkJsonLinter, lintGutter()];

const JSON_BASIC_SETUP = { bracketMatching: true, closeBrackets: true };

const isValidJson = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return true; // empty optional field
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

// Pretty-print valid JSON (2-space indent); leave invalid/empty text untouched so the user's
// in-progress draft is never destroyed.
const prettyJson = (text: string): string => {
  if (!text.trim()) return text;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

interface JsonEditorProps
  extends Pick<
    CodeMirrorEditorProps,
    'value' | 'ariaLabel' | 'readOnly' | 'autoFocus' | 'height' | 'minHeight' | 'maxHeight' | 'error'
  > {
  /** Emits the raw text on every edit (the consumer owns the string, valid or not). */
  onChange: (value: string) => void;
  /** Optional: reports whether the current text parses as JSON (empty counts as valid). */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * JSON editor on top of {@link CodeMirrorEditor}: syntax highlighting, bracket matching, and
 * inline syntax-error markers in the gutter (via @codemirror/lang-json + lint).
 *
 * Displays JSON pretty-printed (2-space indent) for readability: it formats the incoming
 * value on load and whenever the external value changes (e.g. switching records), and
 * re-formats on blur. While typing it keeps the raw text verbatim so the caret never jumps.
 */
const JsonEditor: FC<JsonEditorProps> = ({ value, onChange, onValidityChange, ...rest }) => {
  const [text, setText] = useState(() => prettyJson(value));
  // Tracks the last value we either received externally or pushed upward, so the parent
  // echoing our own onChange back as `value` doesn't trigger a re-format mid-edit.
  const lastSynced = useRef(text);

  // Keep the latest callbacks in refs so the (stable) blur extension never reconfigures the
  // editor — consumers commonly pass fresh inline arrows on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;

  useEffect(() => {
    if (value !== lastSynced.current) {
      const formatted = prettyJson(value);
      lastSynced.current = formatted;
      setText(formatted);
    }
  }, [value]);

  const commit = (next: string) => {
    setText(next);
    lastSynced.current = next;
    onChangeRef.current(next);
    onValidityChangeRef.current?.(isValidJson(next));
  };

  // Format-on-blur: tidy the JSON once the user moves away, reading from the view and writing
  // back through the refs so nothing here is a reactive dependency.
  const blurExtension = useMemo<Extension>(
    () =>
      EditorView.domEventHandlers({
        blur: (_event, view) => {
          const current = view.state.doc.toString();
          const formatted = prettyJson(current);
          if (formatted !== current) {
            lastSynced.current = formatted;
            setText(formatted);
            onChangeRef.current(formatted);
            onValidityChangeRef.current?.(isValidJson(formatted));
          }
          return false;
        },
      }),
    []
  );

  const extensions = useMemo<Extension[]>(() => [...STATIC_EXTENSIONS, blurExtension], [blurExtension]);

  return (
    <CodeMirrorEditor value={text} onChange={commit} extensions={extensions} basicSetup={JSON_BASIC_SETUP} {...rest} />
  );
};

export default JsonEditor;
